import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { App as AntApp, Checkbox, Form, Input } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { ClientType } from "../../../../../client/enums";
import { CalcType } from "../../../../enums";
import { CalcCheckableTag } from "../../../components/recalculation/CalcCheckableTag";
import { CalcDiscountsButton } from "../../../components/recalculation/CalcDiscountsButton";
import { CalcOptionTag } from "../../../components/recalculation/CalcOptionTag";
import { CalcRecalculationCard } from "../../../components/recalculation/CalcRecalculationCard";
import { hasFieldChanges } from "../../../utils";
import {
  AnimalReinsuranceAmount,
  ElementReinsuranceAmount,
  GapDuration,
  GlassReinsuranceAmount,
  InjuryReinsuranceAmount,
  ReplacementVehicleCategory,
} from "../../enums";
import type { VehicleCalc, VehicleCalcCrossSelling, VehicleCalcReinsurancesData, VehicleCalcType } from "../../types";
import { isCrossSellingDiscountAvailable, isReinsuranceAvailable } from "../../utils";

type ReinsuranceName = keyof VehicleCalcReinsurancesData;

type RecalculationForm = {
  reinsurancesData: VehicleCalcReinsurancesData;
  generalData: {
    crossSelling: VehicleCalcCrossSelling;
  };
};

type CrossSellingDiscount = keyof VehicleCalcCrossSelling;

const CROSS_SELLING_DISCOUNTS = [
  "kooperativaContract",
  "unionHealthContract",
  "uniqaContract",
  "pillowDiscountCode",
] satisfies CrossSellingDiscount[];

const CROSS_SELLING_FIELD_NAMES: CrossSellingDiscount[] = [...CROSS_SELLING_DISCOUNTS, "uniqaContractNumber"];

type ReinsuranceField = {
  name: ReinsuranceName;
  hasTooltip?: boolean;
  select?: {
    name: ReinsuranceName;
    labelKey: string;
    optionKeyPrefix: string;
    tagOptionKeyPrefix?: string;
    options: string[];
    mtplOnly?: boolean;
  };
  checkbox?: "gapComplicityReinsurance";
};

const REINSURANCE_FIELDS: ReinsuranceField[] = [
  { name: "extendedAssistance" },
  { name: "theft" },
  { name: "vandalism" },
  {
    name: "glass",
    select: {
      name: "glassAmount",
      labelKey: "calc.vehicle.attrs.reinsurancesData.glassAmountLabel",
      optionKeyPrefix: "calc.vehicle.enums.glassReinsuranceAmount.",
      options: Object.keys(GlassReinsuranceAmount),
    },
  },
  {
    name: "animal",
    select: {
      name: "animalAmount",
      labelKey: "calc.vehicle.attrs.reinsurancesData.animalAmountLabel",
      optionKeyPrefix: "calc.vehicle.enums.animalReinsuranceAmount.",
      options: Object.keys(AnimalReinsuranceAmount),
    },
  },
  {
    name: "element",
    select: {
      name: "elementAmount",
      labelKey: "calc.vehicle.attrs.reinsurancesData.elementAmountLabel",
      optionKeyPrefix: "calc.vehicle.enums.elementReinsuranceAmount.",
      options: Object.keys(ElementReinsuranceAmount),
      mtplOnly: true,
    },
  },
  {
    name: "injury",
    select: {
      name: "injuryAmount",
      labelKey: "calc.vehicle.attrs.reinsurancesData.injuryAmountLabel",
      optionKeyPrefix: "calc.vehicle.enums.injuryReinsuranceAmount.",
      options: Object.keys(InjuryReinsuranceAmount),
      mtplOnly: true,
    },
  },
  {
    name: "gap",
    select: {
      name: "gapDuration",
      labelKey: "calc.vehicle.enums.gapDuration._label",
      optionKeyPrefix: "calc.vehicle.enums.gapDuration.",
      options: [GapDuration.MONTHS_24, GapDuration.MONTHS_36, GapDuration.MONTHS_48, GapDuration.MONTHS_60],
    },
    checkbox: "gapComplicityReinsurance",
  },
  {
    name: "replacementVehicle",
    select: {
      name: "replacementVehicleCategory",
      labelKey: "calc.vehicle.enums.replacementVehicleCategory._label",
      optionKeyPrefix: "calc.vehicle.enums.replacementVehicleCategory.",
      tagOptionKeyPrefix: "calc.vehicle.enums.replacementVehicleCategoryShort.",
      options: Object.keys(ReplacementVehicleCategory),
    },
  },
  { name: "generaliAbroadVehicleRepair", hasTooltip: true },
  { name: "koopExtendedWarranty" },
  { name: "generaliElectroPackage", hasTooltip: true },
];

const REINSURANCE_FIELD_NAMES = REINSURANCE_FIELDS.flatMap((field) => [
  field.name,
  field.select?.name,
  field.checkbox,
]).filter(isDefined);

const clearInactiveValues = (data: VehicleCalcReinsurancesData): VehicleCalcReinsurancesData => ({
  ...data,
  glassAmount: data.glass ? data.glassAmount : undefined,
  animalAmount: data.animal ? data.animalAmount : undefined,
  elementAmount: data.element ? data.elementAmount : undefined,
  injuryAmount: data.injury ? data.injuryAmount : undefined,
  gapDuration: data.gap ? data.gapDuration : undefined,
  gapComplicityReinsurance: data.gap ? data.gapComplicityReinsurance : undefined,
  replacementVehicleCategory: data.replacementVehicle ? data.replacementVehicleCategory : undefined,
});

const normalizeCrossSelling = ({
  uniqaContractNumber,
  pillowDiscountCode,
  ...crossSelling
}: VehicleCalcCrossSelling): VehicleCalcCrossSelling => ({
  ...crossSelling,
  uniqaContract: !!uniqaContractNumber?.trim(),
  uniqaContractNumber: uniqaContractNumber?.trim() || undefined,
  pillowDiscountCode: pillowDiscountCode || undefined,
});

const resolveSelect = (field: ReinsuranceField, calcType: VehicleCalcType) =>
  field.select && (!field.select.mtplOnly || calcType === CalcType.MTPL) ? field.select : undefined;

type CrossSellingDiscountsProps = {
  calcType: VehicleCalcType;
  holderType?: ClientType;
};

const CrossSellingDiscounts = ({ calcType, holderType }: CrossSellingDiscountsProps) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<RecalculationForm>();
  const crossSelling = Form.useWatch(["generalData", "crossSelling"], form);

  const normalizedCrossSelling = normalizeCrossSelling(crossSelling ?? {});
  const activeCount = CROSS_SELLING_DISCOUNTS.filter(
    (discount) => isCrossSellingDiscountAvailable(discount, calcType, holderType) && !!normalizedCrossSelling[discount],
  ).length;

  return (
    <CalcDiscountsButton count={activeCount} title={t("calc.vehicle.sections.crossSelling")}>
      {isCrossSellingDiscountAvailable("kooperativaContract", calcType, holderType) && (
        <Form.Item
          name={["generalData", "crossSelling", "kooperativaContract"]}
          valuePropName="checked"
          style={{ marginBottom: 0 }}
        >
          <Checkbox>{t("calc.vehicle.attrs.generalData.crossSelling.kooperativaContract")}</Checkbox>
        </Form.Item>
      )}

      {isCrossSellingDiscountAvailable("unionHealthContract", calcType, holderType) && (
        <Form.Item
          name={["generalData", "crossSelling", "unionHealthContract"]}
          valuePropName="checked"
          style={{ marginBottom: 0 }}
        >
          <Checkbox>{t("calc.vehicle.attrs.generalData.crossSelling.unionHealthContract")}</Checkbox>
        </Form.Item>
      )}

      <Form.Item
        name={["generalData", "crossSelling", "uniqaContractNumber"]}
        label={t("calc.vehicle.attrs.generalData.crossSelling.uniqaContractNumber")}
        rules={[validations.size(1, 64)]}
        style={{ marginBottom: 0 }}
      >
        <Input allowClear />
      </Form.Item>

      <Form.Item
        name={["generalData", "crossSelling", "pillowDiscountCode"]}
        label={t("calc.vehicle.attrs.generalData.crossSelling.pillowDiscountCode")}
        rules={[validations.size(1, 64)]}
        style={{ marginBottom: 0 }}
      >
        <Input allowClear />
      </Form.Item>
    </CalcDiscountsButton>
  );
};

type Props = {
  calcData: VehicleCalc;
  onRecalculate: (reinsurancesData: VehicleCalcReinsurancesData, crossSelling: VehicleCalcCrossSelling) => void;
};

export const VehicleCalcRecalculationForm = ({ calcData, onRecalculate }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [form] = Form.useForm<RecalculationForm>();
  const values = Form.useWatch([], form);

  useEffect(() => {
    form.resetFields();
  }, [calcData.reinsurancesData, calcData.generalData.crossSelling]);

  const reinsuranceFields = REINSURANCE_FIELDS.filter((field) =>
    isReinsuranceAvailable(field.name, calcData.type, calcData.vehicleData.category, calcData.vehicleData.fuelType),
  );

  const isReinsurancesChanged =
    !!values?.reinsurancesData &&
    hasFieldChanges(
      REINSURANCE_FIELD_NAMES,
      clearInactiveValues(values.reinsurancesData),
      clearInactiveValues(calcData.reinsurancesData),
    );

  const isCrossSellingChanged =
    !!values?.generalData?.crossSelling &&
    hasFieldChanges(
      CROSS_SELLING_FIELD_NAMES,
      normalizeCrossSelling(values.generalData.crossSelling),
      normalizeCrossSelling(calcData.generalData.crossSelling),
    );

  const isChanged = isReinsurancesChanged || isCrossSellingChanged;

  const tagFieldNames = reinsuranceFields
    .filter((field) => !resolveSelect(field, calcData.type))
    .map((field) => field.name);

  const hiddenFieldNames = [
    ...new Set([
      ...(Object.keys(calcData.reinsurancesData) as ReinsuranceName[]),
      ...REINSURANCE_FIELDS.flatMap((field) => [field.name, field.select?.name, field.checkbox]).filter(isDefined),
    ]),
  ].filter((name) => !tagFieldNames.includes(name));

  const handleRecalculate = () => {
    form.validateFields().then(
      ({ reinsurancesData, generalData }) =>
        onRecalculate(clearInactiveValues(reinsurancesData), normalizeCrossSelling(generalData.crossSelling)),
      () => notification.error({ title: t("common.error"), description: t("common.validations.formError") }),
    );
  };

  return (
    <Form
      form={form}
      name="vehicleCalcRecalculationForm"
      layout="vertical"
      initialValues={{
        reinsurancesData: calcData.reinsurancesData,
        generalData: { crossSelling: calcData.generalData.crossSelling },
      }}
    >
      {hiddenFieldNames.map((name) => (
        <HiddenInput key={name} name={["reinsurancesData", name]} />
      ))}

      {CROSS_SELLING_FIELD_NAMES.map((name) => (
        <HiddenInput key={name} name={["generalData", "crossSelling", name]} />
      ))}

      <CalcRecalculationCard
        title={t("calc.vehicle.sections.reinsurancesData")}
        isChanged={isChanged}
        onRecalculate={handleRecalculate}
        discounts={
          <CrossSellingDiscounts
            calcType={calcData.type}
            holderType={calcData.clientsData.clients[calcData.clientsData.holderIndex]?.client.type}
          />
        }
      >
        {reinsuranceFields.map((field) => {
          const select = resolveSelect(field, calcData.type);
          const { checkbox } = field;
          const label = t(`calc.vehicle.attrs.reinsurancesData.${field.name}`);

          return select ? (
            <CalcOptionTag
              key={field.name}
              name={["reinsurancesData", field.name]}
              optionName={["reinsurancesData", select.name]}
              label={label}
              optionsLabel={t(select.labelKey)}
              options={select.options.map((option) => ({
                value: option,
                label: t(select.optionKeyPrefix + option),
                tagLabel: select.tagOptionKeyPrefix ? t(select.tagOptionKeyPrefix + option) : undefined,
              }))}
              extra={
                checkbox && (
                  <Form.Item
                    name={["reinsurancesData", checkbox]}
                    valuePropName="checked"
                    rules={[validations.none]}
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox>{t(`calc.vehicle.attrs.reinsurancesData.${checkbox}`)}</Checkbox>
                  </Form.Item>
                )
              }
              // The backend requires this value for some institutions and the field is not filled until it renders.
              onActivate={checkbox ? () => form.setFieldValue(["reinsurancesData", checkbox], false) : undefined}
            />
          ) : (
            <Form.Item key={field.name} name={["reinsurancesData", field.name]} valuePropName="checked" noStyle>
              <CalcCheckableTag>
                {field.hasTooltip ? (
                  <LabelWithTooltip label={label} tooltip={t(`calc.vehicle.helpers.${field.name}Desc`)} />
                ) : (
                  label
                )}
              </CalcCheckableTag>
            </Form.Item>
          );
        })}
      </CalcRecalculationCard>
    </Form>
  );
};
